import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {User, UserAiSettings} from '../interfaces/user.interface';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class UserResource {
  private readonly baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {
  }

  getOne(id: string): Observable<User> {
    return this.http.get<User>(this.baseUrl + '/user/' + id);
  }

  create(user: User): Observable<void> {
    return this.http.post<void>(this.baseUrl + '/user', user);
  }

  update(user: User): Observable<User> {
    return this.http.put<User>(this.baseUrl + '/user', user);
  }

  getFeatures(mapId?: string): Observable<{ canvas3d: boolean; smaplet3d?: boolean }> {
    const query = mapId ? '?mapId=' + encodeURIComponent(mapId) : '';
    return this.http.get<{ canvas3d: boolean; smaplet3d?: boolean }>(this.baseUrl + '/user/features' + query);
  }

  getCurrentLoggedInUser(): Observable<User> {
    return this.http.get<User>(this.baseUrl + '/user/current/logged-in');
  }

  login(login: { email: string, password: string }): Observable<{ accessToken: string, user: User }> {
    return this.http.post<{ accessToken: string, user: User }>(this.baseUrl + '/user/login', login);
  }

  signInWithToken(token: string): Observable<{ authToken: string, refreshToken: string, user: User }> {
    return this.http.get<{
      authToken: string,
      refreshToken: string,
      user: User
    }>(this.baseUrl + '/user/signin/' + token);
  }
  /**
   * Request sending of a close account confirmation link to the authenticated user.
   */
  sendCloseAccountLink(): Observable<void> {
    return this.http.post<void>(this.baseUrl + '/user/send-close-account-link', {});
  }
  /**
   * Confirm account closure and delete user if token is valid.
   */
  confirmCloseAccount(token: string): Observable<{ wasPaid: boolean }> {
    return this.http.get<{ wasPaid: boolean }>(
      `${this.baseUrl}/public/user/close-account/${token}`
    );
  }

  sendSignInLink(email: string): Observable<void> {
    return this.http.post<void>(this.baseUrl + '/user/send-signin-link', { email });
  }

  getAvailableUsersForMapSharing(mapId: string, q = ''): Observable<User[]> {
    const params = q ? '?q=' + encodeURIComponent(q) : '';
    return this.http.get<User[]>(this.baseUrl + '/user/available-for-sharing/' + mapId + params);
  }

  getAvailableUsersForTransfer(q = ''): Observable<User[]> {
    const params = q ? '?q=' + encodeURIComponent(q) : '';
    return this.http.get<User[]>(this.baseUrl + '/user/owner/available-for-transfer' + params);
  }

  createPublicUser(dto: {
    name: string;
    lastName: string;
    organization?: string;
    eduDiscount?: boolean;
    bypassTrial?: boolean;
    email: string;
    password?: string;
    planId?: string;
  }): Observable<{
    message: string;
    token?: string;
    user?: User;
  }> {
    return this.http.post<{
      message: string;
      token?: string;
      user?: User;
    }>(this.baseUrl + '/public/user/signup', dto);
  }

  sendWelcomeEmail(userId: string): Observable<void> {
    return this.http.post<void>(this.baseUrl + '/public/user/send-welcome-email', {userId});
  }

  changeEmail(email: string): Observable<void> {
    return this.http.put<void>(this.baseUrl + '/user/email', {email});
  }

  changeBillingName(billingName: string): Observable<void> {
    return this.http.put<void>(this.baseUrl + '/user/billing-name', {billingName});
  }

  changeServicePlan(packageId: string): Observable<void> {
    return this.http.put<void>(this.baseUrl + '/user/service-plan', {packageId});
  }

  changeBillingPeriod(period: 'monthly' | 'yearly'): Observable<void> {
    return this.http.put<void>(this.baseUrl + '/user/billing-period', {period});
  }

  endTrial(): Observable<void> {
    return this.http.post<void>(this.baseUrl + '/user/end-trial', {});
  }

  getAiSettings(): Observable<UserAiSettings> {
    return this.http.get<UserAiSettings>(this.baseUrl + '/user/ai-settings');
  }

  updateAiSettings(updates: Partial<UserAiSettings>): Observable<UserAiSettings> {
    return this.http.put<UserAiSettings>(this.baseUrl + '/user/ai-settings', updates);
  }
}
